import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { formatDate } from 'lib/helpers/dateUtils';
import useTimezone from 'lib/hooks/useTimezone';

import Avatar from 'components/shared/Avatar/Avatar';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import {
  MYRIAD_STAFF_LABEL,
  getArticle,
  getCreatorAvatarProps,
  getCreatorDisplayName
} from '../../../lib/helpers';
import { ACTION_MAP } from '../lib/constants';

import ActivityContent from './ActivityContent';

const AppointmentDetail = ({ activity, timezone }) => {
  const navigate = useNavigate();
  const { id: patientId } = useParams();
  const { appointmentLink, appointmentDate, appointmentProvider } = activity;

  const handleClick = () => {
    if (appointmentLink && patientId) {
      navigate(`/portal/charts/${patientId}?appointment_id=${appointmentLink}`);
    }
  };

  const parts = [
    appointmentLink && (
      <span
        key="id"
        role="link"
        tabIndex={0}
        className="cursor-pointer text-primary-500 hover:underline"
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}>
        #{appointmentLink}
      </span>
    ),
    appointmentDate && (
      <span key="date">{formatDate(appointmentDate, timezone, 'MMM d, yyyy h:mma')}</span>
    ),
    appointmentProvider && <span key="provider">{appointmentProvider}</span>
  ].filter(Boolean);

  return (
    <div className="pl-[9px]">
      <div className="border-0 border-l border-solid border-neutral-300 pl-[13px] text-sm leading-[22px] text-neutral-600">
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="mx-1">·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ActivityLog = ({ category, activity, overwriteActivities }) => {
  const timezone = useTimezone();
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);

  const currentUserId = permission?.admin_id || userState?.id;
  const isViewerAdmin = !!permission?.admin_id;

  const {
    creator,
    created_at,
    prefix = '',
    formattedChanges,
    action,
    isNestedCreate,
    isCreateRecord,
    isDeleteRecord,
    isLifecycleEvent
  } = activity;

  const isCurrentUser = currentUserId && creator?.id === currentUserId;
  const fullName = getCreatorDisplayName({
    creator,
    isAdminAction: activity.created_by_type === 0,
    isViewerAdmin
  });
  const displayName = isCurrentUser ? 'You' : fullName;
  const avatarProps = getCreatorAvatarProps({
    creator,
    isMasked: fullName === MYRIAD_STAFF_LABEL && !isCurrentUser
  });

  const actionLabel = ACTION_MAP[isNestedCreate ? 'create' : action];
  const entityLabel = prefix || category || 'record';

  const showEntityLine = isCreateRecord || isDeleteRecord || isLifecycleEvent;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center gap-1">
          <Avatar
            size="small"
            className="!h-[18px] !min-h-[18px] !w-[18px] !min-w-[18px] shrink-0"
            isSquare={false}
            {...avatarProps}
          />
          <p className="text-sm leading-[22px] text-neutral-600">
            <span className="font-medium text-neutral-800">{displayName} </span>
            {actionLabel}
            {showEntityLine ? (
              <span>
                {isCreateRecord && !activity.entityDetail && !activity.appointmentLink
                  ? ' the'
                  : isCreateRecord && (activity.entityDetail || activity.appointmentLink)
                    ? ' a new'
                    : ` ${getArticle(entityLabel)}`}{' '}
                <span className="font-semibold text-neutral-800">{entityLabel}</span>
              </span>
            ) : (
              prefix && <span className="font-semibold text-neutral-800"> {prefix}</span>
            )}
          </p>
        </div>
        <Typography
          color="neutral-500"
          size="small"
          weight="normal"
          className="shrink-0 whitespace-nowrap">
          {formatDate(created_at, timezone, 'h:mma')}
        </Typography>
      </div>

      {showEntityLine && activity.appointmentLink ? (
        <AppointmentDetail activity={activity} timezone={timezone} />
      ) : showEntityLine && activity.entityDetail ? (
        <div className="pl-[9px]">
          <div className="border-0 border-l border-solid border-neutral-300 pl-[13px] text-sm leading-[22px]">
            <span className="font-semibold text-neutral-800">
              {prefix === 'Insurance profile' ? 'Payer' : 'Name'}:
            </span>{' '}
            <span className="font-semibold text-neutral-800">{activity.entityDetail}</span>
          </div>
        </div>
      ) : (
        <ActivityContent
          overwriteActivities={overwriteActivities}
          formattedChanges={formattedChanges}
          action={action}
          fieldLabels={activity._fieldLabels}
        />
      )}
    </div>
  );
};

export default ActivityLog;
